import React, { useCallback } from "react";
import { useField } from "formik";
import { Flex, ErrorMessage, Text } from "@jsq/ds/core";
import { PayeeFilter } from "../PayeeFilter";
import { PayeeObjectType } from "../PayeeFilter/types";

interface FormPayeeSelectorProps {
  onPayeeSelect: (payee: PayeeObjectType) => void;
  fieldName: string;
  arenaId?: string;
  disabled?: boolean;
  onCreateNewPayee?: () => void;
  selectedPayee?: PayeeObjectType | null;
}

/**
 * Wrapper around the PayeeFilter component that integrates with Formik. Native Formik validation was failing for this component.
 */
export const FormPayeeSelector: React.FC<FormPayeeSelectorProps> = ({
  onPayeeSelect,
  fieldName,
  arenaId,
  disabled = false,
  onCreateNewPayee,
  selectedPayee,
}: FormPayeeSelectorProps) => {
  const [, meta, helpers] = useField(fieldName);

  const onSelectPayee = useCallback(
    (payee?: PayeeObjectType | null) => {
      if (!payee) return;
      void helpers.setValue(payee.id);
      void helpers.setTouched(true);
      void helpers.setError(undefined);
      onPayeeSelect(payee);
    },
    [helpers, onPayeeSelect],
  );

  const handleCreateNewPayee = useCallback(() => {
    onCreateNewPayee?.();
  }, [onCreateNewPayee]);

  const hasError = Boolean(meta.touched && meta.error && !meta.value);

  return (
    <>
      <Flex flexDirection="column" gap={8}>
        <Text variant="regular-md">Payee</Text>
        <PayeeFilter
          key={arenaId || "no-client"}
          onPayeeSelect={onSelectPayee}
          onCreateNewPayee={handleCreateNewPayee}
          error={hasError ? meta.error : ""}
          disabled={disabled}
          arenaId={arenaId}
          selectedPayee={selectedPayee}
        />
        {hasError && <ErrorMessage>Payee is required</ErrorMessage>}
      </Flex>
    </>
  );
};
